import React, { useState, useMemo, useEffect } from 'react';
import './BIModule.css';
import api from '../../utils/api';
import FaturamentoMensalReport from './reports/faturamento';
import CriticidadeFaturamentoReport from './reports/criticidade';
import CteDiarioReport from './reports/cte-diario';

// ─── Types ────────────────────────────────────────────────────────────────────
type Category = 'Todos' | 'Operacional' | 'Faturamento' | 'CTE' | 'Contrato' | 'Frota' | 'Terceiros' | 'Análises';

interface Dashboard {
  id: string;
  title: string;
  description: string;
  category: Exclude<Category, 'Todos'>;
  icon: React.ReactNode;
  color: string;
  dataSource?: 'kpis' | 'tendencias' | 'dashboard-executivo';
}

interface KpisPayload {
  vendas_ano?: number;
  receita_ano?: number;
  clientes_ativos?: number;
  ticket_medio?: number;
}

interface TendenciasPayload {
  ano?: number;
  mes?: number;
  total_vendas?: number;
  receita_mes?: number;
  ticket_medio_mes?: number;
}

interface VendasPayload {
  id?: number;
  data_venda?: string;
  valor_total?: number;
  cliente?: string;
  produto?: string;
  quantidade?: number;
}

interface DashboardExecutivoPayload {
  operacional?: Record<string, number | string | null>;
  nucci?: Record<string, number | string | null>;
  business_intelligence?: Record<string, number | string | null>;
}

// ─── Icon Components ──────────────────────────────────────────────────────────
const IconViagens = () => (
  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="8" fill="#EEF2FF"/>
    <path d="M8 22l4-8 4 4 4-6 4 10" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="24" cy="10" r="3" fill="#6366F1" opacity="0.3"/>
  </svg>
);
const IconOrdens = () => (
  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="8" fill="#F0FDF4"/>
    <rect x="9" y="8" width="14" height="16" rx="2" stroke="#22C55E" strokeWidth="2"/>
    <path d="M12 13h8M12 17h5" stroke="#22C55E" strokeWidth="2" strokeLinecap="round"/>
    <path d="M18 20l2 2 3-3" stroke="#22C55E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconCteSemManifesto = () => (
  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="8" fill="#EFF6FF"/>
    <rect x="9" y="8" width="14" height="16" rx="2" stroke="#3B82F6" strokeWidth="2"/>
    <path d="M12 13h8M12 17h8M12 21h4" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="22" cy="22" r="3" fill="#EFF6FF" stroke="#EF4444" strokeWidth="1.5"/>
    <path d="M21 21l2 2M23 21l-2 2" stroke="#EF4444" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);
const IconStatusCte = () => (
  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="8" fill="#FFF7ED"/>
    <circle cx="16" cy="16" r="7" stroke="#F97316" strokeWidth="2"/>
    <path d="M13 16l2 2 4-4" stroke="#F97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconFatMensal = () => (
  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="8" fill="#F0FDF4"/>
    <path d="M9 23V17l3.5-4 4 5L20 12l3 5v6H9z" fill="#86EFAC" stroke="#16A34A" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>
);
const IconCriticidadeFat = () => (
  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="8" fill="#FFF1F2"/>
    <path d="M16 9l1.8 5.5H23l-4.5 3.3 1.7 5.2L16 20l-4.2 3 1.7-5.2L9 14.5h5.2L16 9z" fill="#FDA4AF" stroke="#E11D48" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>
);
const IconFrota = () => (
  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="8" fill="#FFF7ED"/>
    <rect x="7" y="14" width="18" height="9" rx="2" stroke="#F97316" strokeWidth="2"/>
    <path d="M7 17h18" stroke="#F97316" strokeWidth="1.5"/>
    <circle cx="11" cy="24" r="2" fill="#FFF7ED" stroke="#F97316" strokeWidth="1.5"/>
    <circle cx="21" cy="24" r="2" fill="#FFF7ED" stroke="#F97316" strokeWidth="1.5"/>
    <path d="M11 14v-4h5l4 4" stroke="#F97316" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>
);
const IconTerceiros = () => (
  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="8" fill="#F5F3FF"/>
    <circle cx="16" cy="13" r="4" stroke="#8B5CF6" strokeWidth="2"/>
    <path d="M9 24c0-3.866 3.134-7 7-7s7 3.134 7 7" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IconContratos = () => (
  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="8" fill="#FEF2F2"/>
    <rect x="10" y="8" width="12" height="16" rx="2" stroke="#EF4444" strokeWidth="2"/>
    <path d="M13 13h6M13 17h4" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
    <path d="M13 21h2" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IconCteBloqueado = () => (
  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="8" fill="#FFFBEB"/>
    <rect x="10" y="8" width="12" height="16" rx="2" stroke="#F59E0B" strokeWidth="2"/>
    <path d="M16 15v4" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="16" cy="21" r="1" fill="#F59E0B"/>
  </svg>
);
const IconSemPlaca = () => (
  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="8" fill="#F0FDF4"/>
    <rect x="8" y="13" width="16" height="8" rx="2" stroke="#16A34A" strokeWidth="2"/>
    <path d="M11 17h10" stroke="#16A34A" strokeWidth="2" strokeLinecap="round"/>
    <path d="M8 11l16 12" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
const IconFatDiario = () => (
  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="8" fill="#EFF6FF"/>
    <rect x="9" y="10" width="14" height="13" rx="2" stroke="#3B82F6" strokeWidth="2"/>
    <path d="M9 14h14" stroke="#3B82F6" strokeWidth="1.5"/>
    <path d="M13 10V8M19 10V8" stroke="#3B82F6" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M12 18l2 2 5-4" stroke="#3B82F6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconFatAnualGrupo = () => (
  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="8" fill="#FFF7ED"/>
    <rect x="9" y="18" width="3" height="6" fill="#FB923C"/>
    <rect x="14" y="14" width="3" height="10" fill="#F97316"/>
    <rect x="19" y="10" width="3" height="14" fill="#EA580C"/>
  </svg>
);
const IconFatAnualFrota = () => (
  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="8" fill="#F0FDF4"/>
    <path d="M8 22c2-6 4-2 6-6s4-4 6-2 4 2 4-4" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconCriticidade2 = () => (
  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="8" fill="#EFF6FF"/>
    <circle cx="13" cy="13" r="4" fill="#BFDBFE" stroke="#3B82F6" strokeWidth="1.5"/>
    <circle cx="21" cy="19" r="4" fill="#BFDBFE" stroke="#3B82F6" strokeWidth="1.5"/>
    <path d="M15 17l4-2" stroke="#3B82F6" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
const IconMais = () => (
  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="8" fill="#F5F3FF"/>
    <rect x="8" y="8" width="6" height="6" rx="1" fill="#A78BFA"/>
    <rect x="18" y="8" width="6" height="6" rx="1" fill="#A78BFA"/>
    <rect x="8" y="18" width="6" height="6" rx="1" fill="#A78BFA"/>
    <rect x="18" y="18" width="6" height="6" rx="1" fill="#A78BFA"/>
  </svg>
);

// ─── Dashboard Data ───────────────────────────────────────────────────────────
const DASHBOARDS: Dashboard[] = [
  {
    id: 'visao-viagens',
    title: 'Visão Geral das Viagens',
    description: 'Acompanhe os principais indicadores operacionais da FKS Logistics.',
    category: 'Operacional',
    icon: <IconViagens />,
    color: '#6366F1',
    dataSource: 'dashboard-executivo',
  },
  {
    id: 'ordens-coleta',
    title: 'Ordens de Coleta',
    description: 'Análise detalhada das ordens de coleta com e sem nota fiscal.',
    category: 'Operacional',
    icon: <IconOrdens />,
    color: '#22C55E',
  },
  {
    id: 'cte-sem-manifesto',
    title: 'CTE - Sem Manifesto',
    description: "Controle e acompanhamento de CTE's emitidos sem manifesto.",
    category: 'CTE',
    icon: <IconCteSemManifesto />,
    color: '#3B82F6',
  },
  {
    id: 'status-cte',
    title: 'Status do CTE',
    description: "Visão geral dos status dos CTE's emitidos e suas movimentações.",
    category: 'CTE',
    icon: <IconStatusCte />,
    color: '#F97316',
  },
  {
    id: 'faturamento-mensal',
    title: 'Faturamento Mensal FKS',
    description: 'Faturamento mensal detalhado por grupo e seus componentes.',
    category: 'Faturamento',
    icon: <IconFatMensal />,
    color: '#16A34A',
    dataSource: 'kpis',
  },
  {
    id: 'criticidade-faturamento',
    title: 'Criticidade do Faturamento de Veículos',
    description: 'Análise da criticidade do faturamento de veículos por responsável.',
    category: 'Faturamento',
    icon: <IconCriticidadeFat />,
    color: '#E11D48',
  },
  {
    id: 'utilizacao-frota',
    title: 'Análise Utilização da Frota',
    description: 'Análise completa da utilização da frota e performance operacional.',
    category: 'Frota',
    icon: <IconFrota />,
    color: '#F97316',
  },
  {
    id: 'faturamento-terceiros',
    title: 'Faturamento Terceiros',
    description: 'Faturamento detalhado de terceiros por tipo de veículo.',
    category: 'Terceiros',
    icon: <IconTerceiros />,
    color: '#8B5CF6',
  },
  {
    id: 'relatorio-contratos',
    title: 'Relatório de Contratos',
    description: 'Relatório completo de contratos ativos e suas movimentações.',
    category: 'Contrato',
    icon: <IconContratos />,
    color: '#EF4444',
  },
  {
    id: 'cte-fat-bloqueado',
    title: 'CTE com Faturamento Bloqueado',
    description: "Análise de CTE's com faturamento bloqueado e motivos.",
    category: 'CTE',
    icon: <IconCteBloqueado />,
    color: '#F59E0B',
  },
  {
    id: 'sem-placa',
    title: 'Faturamento sem Placa Cadastrada',
    description: 'Análise de faturamento sem placa cadastrada.',
    category: 'Faturamento',
    icon: <IconSemPlaca />,
    color: '#16A34A',
  },
  {
    id: 'fat-cte-diario',
    title: 'Faturamento CTE FKS Diário',
    description: "Acompanhamento diário do faturamento de CTE's da FKS.",
    category: 'Faturamento',
    icon: <IconFatDiario />,
    color: '#3B82F6',
    dataSource: 'tendencias',
  },
  {
    id: 'fat-anual-grupo',
    title: 'Faturamento Anual FKS por Grupo',
    description: 'Evolução anual do faturamento agrupado por grupo.',
    category: 'Faturamento',
    icon: <IconFatAnualGrupo />,
    color: '#F97316',
  },
  {
    id: 'fat-anual-frota',
    title: 'Faturamento Anual Frota',
    description: 'Evolução anual do faturamento da frota.',
    category: 'Frota',
    icon: <IconFatAnualFrota />,
    color: '#16A34A',
  },
  {
    id: 'criticidade-veiculos',
    title: 'Criticidade do Faturamento de Veículos',
    description: 'Dashboard de criticidade detalhado por responsável e veículo.',
    category: 'Faturamento',
    icon: <IconCriticidade2 />,
    color: '#3B82F6',
  },
  {
    id: 'mais-dashboards',
    title: 'Mais Dashboards',
    description: 'Explore outros dashboards disponíveis no sistema.',
    category: 'Análises',
    icon: <IconMais />,
    color: '#8B5CF6',
  },
];

const CATEGORIES: Category[] = [
  'Todos', 'Operacional', 'Faturamento', 'CTE', 'Contrato', 'Frota', 'Terceiros', 'Análises',
];

const CATEGORY_COLORS: Record<string, string> = {
  Operacional: '#2563EB',
  Faturamento: '#16A34A',
  CTE: '#3B82F6',
  Contrato: '#EF4444',
  Frota: '#F97316',
  Terceiros: '#8B5CF6',
  Análises: '#6366F1',
  Todos: '#6B7280',
};

const CATEGORY_BG: Record<string, string> = {
  Operacional: '#EFF6FF',
  Faturamento: '#F0FDF4',
  CTE: '#EFF6FF',
  Contrato: '#FEF2F2',
  Frota: '#FFF7ED',
  Terceiros: '#F5F3FF',
  Análises: '#EEF2FF',
  Todos: '#F3F4F6',
};

// ─── Main Component ───────────────────────────────────────────────────────────
const BIModule: React.FC = () => {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<Category>('Todos');
  const [favorites, setFavorites] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('bi_favorites');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const [selectedDashboard, setSelectedDashboard] = useState<Dashboard | null>(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState('');
  const [dataKpis, setDataKpis] = useState<KpisPayload | null>(null);
  const [dataTendencias, setDataTendencias] = useState<TendenciasPayload[]>([]);
  const [dataExecutivo, setDataExecutivo] = useState<DashboardExecutivoPayload | null>(null);
  const [dataVendas, setDataVendas] = useState<VendasPayload[]>([]);

  const toggleFavorite = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      localStorage.setItem('bi_favorites', JSON.stringify([...next]));
      return next;
    });
  };

  const filtered = useMemo(() => {
    return DASHBOARDS.filter(d => {
      const matchCat = activeCategory === 'Todos' || d.category === activeCategory;
      const matchSearch =
        d.title.toLowerCase().includes(search.toLowerCase()) ||
        d.description.toLowerCase().includes(search.toLowerCase());
      const matchFav = !showOnlyFavorites || favorites.has(d.id);
      return matchCat && matchSearch && matchFav;
    });
  }, [activeCategory, search, showOnlyFavorites, favorites]);

  useEffect(() => {
    if (selectedDashboard?.category === 'Faturamento') {
      setDataLoading(false);
      setDataError('');
      setDataKpis(null);
      setDataTendencias([]);
      setDataExecutivo(null);
      setDataVendas([]);
      return;
    }

    const source = selectedDashboard?.dataSource;

    setDataError('');
    setDataKpis(null);
    setDataTendencias([]);
    setDataExecutivo(null);
    setDataVendas([]);

    if (!source) {
      setDataLoading(false);
      return;
    }

    const loadData = async () => {
      setDataLoading(true);
      try {
        if (source === 'kpis') {
          const response = await api.get('/kpis');
          setDataKpis(response.data?.kpis || null);
        } else if (source === 'tendencias') {
          const response = await api.get('/tendencias');
          setDataTendencias(Array.isArray(response.data?.tendencias) ? response.data.tendencias : []);
        } else if (source === 'dashboard-executivo') {
          const response = await api.get('/dashboard-executivo');
          setDataExecutivo(response.data?.dashboard || null);
        }

        if (selectedDashboard?.category === 'Faturamento') {
          const hoje = new Date();
          const inicio = new Date(hoje.getTime() - 1000 * 60 * 60 * 24 * 30);

          const toDateParam = (d: Date) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
          };

          const responseVendas = await api.get('/vendas', {
            params: {
              dataInicio: toDateParam(inicio),
              dataFim: toDateParam(hoje),
            },
          });
          setDataVendas(Array.isArray(responseVendas.data?.data) ? responseVendas.data.data : []);
        }
      } catch (error: any) {
        const message = error?.response?.data?.message || 'Nao foi possivel carregar os dados BI no momento.';
        setDataError(message);
      } finally {
        setDataLoading(false);
      }
    };

    loadData();
  }, [selectedDashboard]);

  const formatNumber = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === '') return '-';
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return String(value);
    return new Intl.NumberFormat('pt-BR').format(numeric);
  };

  const formatCurrency = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === '') return '-';
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return String(value);
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 2,
    }).format(numeric);
  };

  const renderValue = (key: string, value: number | string | null | undefined) => {
    const normalizedKey = key.toLowerCase();
    if (normalizedKey.includes('receita') || normalizedKey.includes('valor') || normalizedKey.includes('ticket')) {
      return formatCurrency(value);
    }
    return formatNumber(value);
  };

  const formatDate = (value: string | undefined) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('pt-BR');
  };

  const renderDataPanel = () => {
    if (selectedDashboard?.id === 'faturamento-mensal' || selectedDashboard?.id === 'sem-placa') {
      return (
        <FaturamentoMensalReport
          dashboardId={selectedDashboard.id}
          dashboardTitle={selectedDashboard.title}
        />
      );
    }

    if (selectedDashboard?.id === 'criticidade-faturamento' || selectedDashboard?.id === 'criticidade-veiculos') {
      return <CriticidadeFaturamentoReport />;
    }

    if (selectedDashboard?.id === 'fat-cte-diario') {
      return <CteDiarioReport />;
    }

    if (!selectedDashboard?.dataSource) return null;

    if (dataLoading) {
      return <div className="bi-data-status">Carregando dados do BI...</div>;
    }

    if (dataError) {
      return <div className="bi-data-error">{dataError}</div>;
    }

    if (selectedDashboard.dataSource === 'kpis' && dataKpis) {
      const entries = Object.entries(dataKpis);
      if (!entries.length) {
        return <div className="bi-data-status">Nenhum KPI retornado para este dashboard.</div>;
      }

      return (
        <div className="bi-kpi-grid">
          {entries.map(([key, value]) => (
            <div className="bi-kpi-card" key={key}>
              <span className="bi-kpi-label">{key.replace(/_/g, ' ')}</span>
              <strong className="bi-kpi-value">{renderValue(key, value)}</strong>
            </div>
          ))}
        </div>
      );
    }

    if (selectedDashboard.dataSource === 'tendencias') {
      if (!dataTendencias.length) {
        return <div className="bi-data-status">Sem linhas de tendencia para o periodo consultado.</div>;
      }

      return (
        <div className="bi-data-stack">
          <div className="bi-table-wrap">
            <table className="bi-table">
              <thead>
                <tr>
                  <th>Ano</th>
                  <th>Mes</th>
                  <th>Total Vendas</th>
                  <th>Receita Mes</th>
                  <th>Ticket Medio</th>
                </tr>
              </thead>
              <tbody>
                {dataTendencias.map((item, index) => (
                  <tr key={`${item.ano || 0}-${item.mes || 0}-${index}`}>
                    <td>{formatNumber(item.ano)}</td>
                    <td>{formatNumber(item.mes)}</td>
                    <td>{formatNumber(item.total_vendas)}</td>
                    <td>{formatCurrency(item.receita_mes)}</td>
                    <td>{formatCurrency(item.ticket_medio_mes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {dataVendas.length > 0 && (
            <div className="bi-table-wrap">
              <table className="bi-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Cliente</th>
                    <th>Produto</th>
                    <th>Quantidade</th>
                    <th>Valor Total</th>
                  </tr>
                </thead>
                <tbody>
                  {dataVendas.slice(0, 20).map((item, index) => (
                    <tr key={`${item.id || 0}-${index}`}>
                      <td>{formatDate(item.data_venda)}</td>
                      <td>{item.cliente || '-'}</td>
                      <td>{item.produto || '-'}</td>
                      <td>{formatNumber(item.quantidade)}</td>
                      <td>{formatCurrency(item.valor_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      );
    }

    if (selectedDashboard.dataSource === 'dashboard-executivo' && dataExecutivo) {
      const blocos = [
        { key: 'operacional', title: 'Operacional', value: dataExecutivo.operacional || {} },
        { key: 'nucci', title: 'Nucci', value: dataExecutivo.nucci || {} },
        { key: 'business_intelligence', title: 'Business Intelligence', value: dataExecutivo.business_intelligence || {} },
      ];

      return (
        <div className="bi-exec-grid">
          {blocos.map(bloco => (
            <div className="bi-exec-card" key={bloco.key}>
              <h4>{bloco.title}</h4>
              <div className="bi-exec-list">
                {Object.entries(bloco.value).length === 0 ? (
                  <span className="bi-data-muted">Sem dados</span>
                ) : (
                  Object.entries(bloco.value).map(([key, value]) => (
                    <div className="bi-exec-row" key={key}>
                      <span>{key.replace(/_/g, ' ')}</span>
                      <strong>{renderValue(key, value as number | string | null | undefined)}</strong>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }

    return <div className="bi-data-status">Este dashboard ainda nao possui fonte de dados configurada.</div>;
  };

  // ── Dashboard viewer ──
  if (selectedDashboard) {
    return (
      <div className="bi-viewer">
        <div className="bi-viewer-header">
          <button className="bi-back-btn" onClick={() => setSelectedDashboard(null)}>
            ← Voltar
          </button>
          <div className="bi-viewer-title">
            <span className="bi-viewer-icon">{selectedDashboard.icon}</span>
            <div>
              <h2>{selectedDashboard.title}</h2>
              <span
                className="bi-category-badge"
                style={{
                  background: CATEGORY_BG[selectedDashboard.category],
                  color: CATEGORY_COLORS[selectedDashboard.category],
                }}
              >
                {selectedDashboard.category}
              </span>
            </div>
          </div>
        </div>
        <div className="bi-viewer-body">
          <div className="bi-content-stack">
            {renderDataPanel()}
            {selectedDashboard.category !== 'Faturamento' && selectedDashboard?.id !== 'criticidade-faturamento' && selectedDashboard?.id !== 'criticidade-veiculos' && selectedDashboard?.id !== 'fat-cte-diario' && (
              <div className="bi-placeholder">
                <div className="bi-placeholder-icon">{selectedDashboard.icon}</div>
                <h3>{selectedDashboard.title}</h3>
                <p>{selectedDashboard.description}</p>
                <p className="bi-placeholder-hint">
                  Versao web ativa: os dados sao carregados diretamente pelas APIs do modulo BI, sem embed do Power BI.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Catalog ──
  return (
    <div className="bi-module">
      {/* ── Top Bar ── */}
      <div className="bi-topbar">
        <div className="bi-search-wrapper">
          <svg className="bi-search-icon" viewBox="0 0 20 20" fill="none">
            <circle cx="9" cy="9" r="6" stroke="#9CA3AF" strokeWidth="2"/>
            <path d="M13.5 13.5L17 17" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            className="bi-search"
            placeholder="Buscar dashboard..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <button
          className={`bi-favorites-btn${showOnlyFavorites ? ' active' : ''}`}
          onClick={() => setShowOnlyFavorites(v => !v)}
        >
          <svg viewBox="0 0 20 20" fill={showOnlyFavorites ? '#F59E0B' : 'none'} xmlns="http://www.w3.org/2000/svg">
            <path d="M10 2l2.4 4.9 5.4.8-3.9 3.8.92 5.4L10 14.3l-4.82 2.6.92-5.4L2.2 7.7l5.4-.8L10 2z"
              stroke="#F59E0B" strokeWidth="1.5" strokeLinejoin="round"/>
          </svg>
          Minhas Favoritas
        </button>
      </div>

      {/* ── Categories ── */}
      <div className="bi-section">
        <h2 className="bi-section-title">Categorias</h2>
        <div className="bi-categories">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              className={`bi-cat-btn${activeCategory === cat ? ' active' : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat !== 'Todos' && (
                <span
                  className="bi-cat-dot"
                  style={{ background: CATEGORY_COLORS[cat] }}
                />
              )}
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* ── Dashboards Grid ── */}
      <div className="bi-section">
        <h2 className="bi-section-title">Dashboards Disponíveis</h2>
        {filtered.length === 0 ? (
          <div className="bi-empty">
            <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" width="64" height="64">
              <circle cx="32" cy="32" r="28" fill="#F3F4F6"/>
              <path d="M22 32h20M32 22v20" stroke="#D1D5DB" strokeWidth="3" strokeLinecap="round"/>
            </svg>
            <p>Nenhum dashboard encontrado.</p>
          </div>
        ) : (
          <div className="bi-grid">
            {filtered.map(dash => (
              <div
                key={dash.id}
                className="bi-card"
                onClick={() => setSelectedDashboard(dash)}
              >
                <div className="bi-card-main">
                  <div className="bi-card-icon">{dash.icon}</div>
                  <div className="bi-card-info">
                    <h3 className="bi-card-title">{dash.title}</h3>
                    <p className="bi-card-desc">{dash.description}</p>
                  </div>
                </div>
                <div className="bi-card-footer">
                  <span
                    className="bi-category-badge"
                    style={{
                      background: CATEGORY_BG[dash.category],
                      color: CATEGORY_COLORS[dash.category],
                    }}
                  >
                    {dash.category}
                  </span>
                  <div className="bi-card-actions">
                    <button
                      className={`bi-star-btn${favorites.has(dash.id) ? ' favorited' : ''}`}
                      onClick={e => toggleFavorite(dash.id, e)}
                      title={favorites.has(dash.id) ? 'Remover favorito' : 'Adicionar favorito'}
                    >
                      <svg viewBox="0 0 20 20" fill={favorites.has(dash.id) ? '#F59E0B' : 'none'} xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 2l2.4 4.9 5.4.8-3.9 3.8.92 5.4L10 14.3l-4.82 2.6.92-5.4L2.2 7.7l5.4-.8L10 2z"
                          stroke={favorites.has(dash.id) ? '#F59E0B' : '#9CA3AF'} strokeWidth="1.5" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button className="bi-arrow-btn" onClick={() => setSelectedDashboard(dash)}>
                      <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 4l6 6-6 6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BIModule;
